import { template as template_c987f472bef14cbb8fcc857924cda915 } from "@ember/template-compiler";
import { service } from '@ember/service';
import { task, waitForProperty } from 'ember-concurrency';
import { action } from '@ember/object';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { uniqBy, pluck, flatten, pipe, prop, add, subtract } from 'ramda';
import { isTesting, macroCondition } from '@embroider/macros';
import ConfigurationHistoryTypes from 'eflex/constants/configuration-history-types';
import { includes } from '@eflexsystems/ember-composable-helpers';
import { fn } from '@ember/helper';
import { eq, gt, not, or, lte, gte } from 'ember-truth-helpers';
import { on } from '@ember/modifier';
import { inputVal } from 'eflex/helpers';
import { t } from 'ember-intl';
import CheckboxInput from 'eflex/components/checkbox-input';
import LibraryCard from 'eflex/components/work-instruction-editor/library-card';
import StandardModal from 'eflex/components/standard-modal';
import BsButton from 'eflex/components/bs-button';
import Approvers from 'eflex/components/work-instruction-editor/approvers';
import Portal from 'ember-stargate/components/portal';
import ConfigurationHistoryNotes from 'eflex/components/modals/configuration-history-notes';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
export default class ApprovalView extends Component {
    @service
    currentUser;
    @service
    workInstructionRepo;
    @service
    systemConfig;
    @tracked
    enlargedImageSrc;
    @tracked
    approveRejectMode;
    @tracked
    showApproveRejectModal = false;
    @tracked
    showDeploymentModal = false;
    @tracked
    showConfigurationHistoryNotesModal = false;
    @tracked
    currentIndex = 0;
    wieConfigurationSettingsType = ConfigurationHistoryTypes.WIE;
    get carouselTransitionDuration() {
        if (globalThis.isIntegrationTest) {
            return 0;
        }
        if (macroCondition(isTesting())) {
            return 0;
        }
        return 600;
    }
    get approvableWorkInstructions() {
        const { user } = this.currentUser;
        if (user.isAdmin) {
            return this.args.selectableLibraryItems;
        } else if (isPresent(user.wieApproverTags) || isPresent(user.wieDeployerTags)) {
            return this.args.selectableLibraryItems.filter((instruction)=>{
                return this.workInstructionRepo.userCanApprove(instruction) || this.workInstructionRepo.userCanDeploy(instruction);
            });
        } else if (isPresent(user.wieEditorTags)) {
            return this.args.selectableLibraryItems.filter((instruction)=>{
                return this.workInstructionRepo.userCanEdit(instruction);
            });
        }
        return null;
    }
    get approveRejectModalText() {
        const key = `wieList.approveModal.${this.approveRejectMode}`;
        if (this.args.selectedCards.length === 1) {
            return `${key}.single`;
        } else {
            return `${key}.multiple`;
        }
    }
    get approvalsModified() {
        return this.args.selectedCards.some((item)=>item.isDirty);
    }
    get userIsApprover() {
        return this.currentUser.user.wieApproverTags?.length > 0;
    }
    get userIsDeployer() {
        return this.currentUser.user.wieDeployerTags?.length > 0;
    }
    canApprove = (scope)=>{
        return this.workInstructionRepo.userCanApproveScope(scope);
    };
    canDeploy = (scope)=>{
        return this.workInstructionRepo.userCanDeployScope(scope) && this.scopeIsDeployable(scope);
    };
    scopeIsDeployable = (scope)=>{
        return Array.isArray(scope) ? scope.every((workInstruction)=>workInstruction.isApproved) : scope.isApproved;
    };
    get uniqueAssignedLocations() {
        return pipe(pluck('allUniqueAssignedStations'), flatten, uniqBy(prop('id')))(this.args.selectedCards ?? []);
    }
    _closeReviewModal() {
        Object.assign(this, {
            approveRejectMode: null,
            showApproveRejectModal: false
        });
    }
    saveApprovals = task(waitFor(async ()=>{
        await this.workInstructionRepo.saveWithApprovals.linked().perform(this.args.selectedCards);
        this._closeReviewModal();
    }));
    approveSelected = task(waitFor(async ()=>{
        await this.workInstructionRepo.approveAndSave.linked().perform(this.args.selectedCards);
        this._closeReviewModal();
    }));
    disapproveSelected = task(waitFor(async ()=>{
        await this.workInstructionRepo.rejectAndSave.linked().perform(this.args.selectedCards);
        this._closeReviewModal();
    }));
    deploy = task(waitFor(async ()=>{
        if (this.uniqueAssignedLocations.length > 0 && this.systemConfig.jem?.configurationHistory) {
            this.showConfigurationHistoryNotesModal = true;
            await waitForProperty(this, 'showConfigurationHistoryNotesModal', false);
        }
        await this.workInstructionRepo.deployMany.perform(this.args.selectedCards);
        await this.args.delistInstructions(this.args.selectedCards);
        this.showDeploymentModal = false;
    }));
    openReviewModal = task(waitFor(async (mode)=>{
        await this.workInstructionRepo.setPendingApprovalAndSave.perform(this.args.selectedCards, this.currentUser.user.userName);
        Object.assign(this, {
            approveRejectMode: mode,
            showApproveRejectModal: true
        });
    }));
    @action
    rollbackApprovals() {
        this.args.selectedCards.forEach((selectedCard)=>{
            selectedCard.rollbackAttributes();
        });
        this._closeReviewModal();
    }
    @action
    enlargeImage(src) {
        if (src != null) {
            this.enlargedImageSrc = src;
        }
    }
    @action
    setApproved(workInstruction) {
        workInstruction.currentApproveReject.approve();
    }
    @action
    setRejected(workInstruction) {
        workInstruction.currentApproveReject.reject();
    }
    @action
    cardSelected(instruction, e) {
        this.args.cardSelected(instruction, e);
    }
    @action
    cardDoubleClicked(instruction, e) {
        if (!this.workInstructionRepo.userCanEdit(instruction)) {
            return;
        }
        this.args.cardDoubleClick(instruction, e);
    }
    get currentCard() {
        return this.args.selectedCards?.[this.currentIndex];
    }
    get totalCount() {
        return this.args.selectedCards?.length ?? 0;
    }
    get displayIndex() {
        return this.currentIndex + 1;
    }
    static{
        template_c987f472bef14cbb8fcc857924cda915(`
    <div class="approval-view">
      <div class="wie-library-table column-approvals">
        <div class="sticky-top bg-white d-flex align-items-center z-3">
          <CheckboxInput
            class="library-checkbox-multiple mb-0"
            @checked={{@isAllSelected}}
            @onChange={{@selectAll}}
          />
          <div class="wie-library-table-head fw-bold fs-4 p-1 w-100">
            <span class="wie-library-head-cell">
              &nbsp;
            </span>
            <span class="wie-library-head-cell">
              {{t "wieList.fileName"}}
            </span>
            <span class="wie-library-head-cell">
              &nbsp;
            </span>
            <span class="wie-library-head-cell">
              {{t "controls.criteria.type"}}
            </span>
            <span class="wie-library-head-cell">
              {{t "wieList.author"}}
            </span>
            <span class="wie-library-head-cell">
              {{t "wieList.approvers"}}
            </span>
          </div>
        </div>
        <div class="wie-library-table-body">
          {{#each this.approvableWorkInstructions as |libraryItem|}}
            <div class="test-library-item-wrapper d-flex align-items-center w-100">
              <CheckboxInput
                class="library-checkbox-single mb-1"
                @checked={{libraryItem.isSelected}}
                @onChange={{fn @cardMultiselect libraryItem}}
              />
              <LibraryCard
                class="flex-fill"
                @libraryItem={{libraryItem}}
                @setCardSelectedTrue={{@setCardSelectedTrue}}
                @selectedCards={{@selectedCards}}
                @isSelected={{includes libraryItem @selectedCards}}
                @isActive={{eq libraryItem.id @currentWorkInstruction.id}}
                @isRow={{true}}
                @isApprovalView={{true}}
                @openReviewModal={{this.openReviewModal.perform}}
                @canEdit={{this.canEdit}}
                @canApprove={{this.canApprove}}
                @canDeploy={{this.canDeploy}}
                @openDeployModal={{fn (mut this.showDeploymentModal) true}}
                @multipleSelected={{gt @selectedCards.length 1}}
                {{on "click" (fn this.cardSelected libraryItem)}}
                {{on "dblclick" (fn this.cardDoubleClicked libraryItem)}}
              />
            </div>
          {{/each}}
        </div>
      </div>
    </div>
    {{#if this.showApproveRejectModal}}
      <StandardModal
        class="jem wie-approve-reject-modal"
        @contentClass="h-100"
        @size="lg"
        @onCancel={{this.rollbackApprovals}}
      as |modal|>
        <modal.header
          @text="{{t "workInstruction"}} {{t this.approveRejectMode}}"
        />
        <modal.body>
          {{#if (gt this.totalCount 1)}}
            <div class="row align-items-center justify-content-center mb-3">
              <div class="col-auto">
                <BsButton
                  class="previous-review"
                  @size="sm"
                  @disabled={{lte this.currentIndex 0}}
                  @onClick={{fn (mut this.currentIndex) (subtract this.currentIndex 1)}}
                >
                  <FaIcon @icon="arrow-left" @size="sm" />
                </BsButton>
              </div>
              <div class="col-auto">
                {{t "pagination.single.count"
                  currentRecord=this.displayIndex
                  totalRecords=this.totalCount
                }}
              </div>
              <div class="col-auto">
                <BsButton
                  class="next-review"
                  @size="sm"
                  @disabled={{gte this.currentIndex (subtract this.totalCount 1)}}
                  @onClick={{fn (mut this.currentIndex) (add this.currentIndex 1)}}
                >
                  <FaIcon @icon="arrow-right" @size="sm" />
                </BsButton>
              </div>
            </div>
          {{/if}}
          <div class="row">
            <div class="col wie-approve-reject-slide">
              <div class="slide-panel mb-2">
                <div class="slide-panel-header fw-semibold">
                  {{t "wieList.oldWorkInstruction"}}<div
                    class="icon icon-fullscreen-enter enlarge-image pointer float-end position-relative z-2
                    {{if this.currentCard.deployed "" "disabled opacity-25 "}}"
                    {{on "click" (fn this.enlargeImage this.currentCard.deployedImageUrl)}}
                  ></div>
                </div>
                <div class="instruction-image w-100">
                  {{#if this.currentCard.deployed}}
                    <img src={{this.currentCard.deployedImageUrl}} class="w-100"/>
                  {{else}}
                    <div class="no-image">{{t "jem.wieApprovals.notDeployedYet"}}</div>
                  {{/if}}
                </div>
                <div class="instruction-name">
                  {{this.currentCard.deployedName}}
                </div>
              </div>
              <div class="slide-panel right-panel">
                <div class="slide-panel-header fw-semibold z-2">
                  <div
                    class="icon icon-fullscreen-enter enlarge-image pointer float-end position-relative z-2"
                    {{on "click" (fn this.enlargeImage this.currentCard.displayImageUrl)}}
                  ></div>{{t "wieList.newWorkInstruction"}}
                </div>
                <div class="instruction-image w-100">
                  <img src={{this.currentCard.displayImageUrl}} class="w-100"/>
                </div>
                <div class="instruction-name">
                  {{this.currentCard.name}}
                </div>
                {{#if (eq this.approveRejectMode "review")}}
                  <div
                    class="row g-2 approve-reject-control
                    {{this.currentCard.currentApproveReject.state}}"
                  >
                    <div class="col-auto">
                      <BsButton
                        class="approve-btn"
                        @onClick={{fn this.setApproved this.currentCard}}
                      >
                        {{t 'approve'}}
                      </BsButton>
                    </div>
                    <div class="col-auto">
                      <BsButton
                        class="reject-btn"
                        @onClick={{fn this.setRejected this.currentCard}}
                      >
                        {{t 'reject'}}
                      </BsButton>
                    </div>
                  </div>
                {{/if}}
              </div>
            </div>
            <div class="row wie-approve-reject-detail">
              <div class="col instruction-text">
                {{t this.approveRejectModalText}}
                <div class="approvers fw-bold pt-3">
                  <div class="title">
                    {{t "wieList.approvers"}}
                  </div>
                  <Approvers
                    @workInstruction={{this.currentCard}}
                  />
                </div>
              </div>
              <div class="col note-container">
                <label for="approve-reject-notes" class="note-label">
                  {{t "wieList.approveModal.note"}}
                </label>
                <textarea
                  class="form-control approve-reject-notes"
                  value={{this.currentCard.currentApproveReject.notes}}
                  {{on "input" (inputVal (fn (mut this.currentCard.currentApproveReject.notes)))}}
                />
              </div>
            </div>
          </div>
        </modal.body>
        <modal.footer as |footer|>
          {{#if (eq this.approveRejectMode "review")}}
            <BsButton
              @type="primary"
              @disabled={{not this.approvalsModified}}
              @onClick={{this.saveApprovals.perform}}
              class="apply-review fs-3"
            >
              {{t "apply"}}
            </BsButton>
          {{/if}}
          {{#if (eq this.approveRejectMode "approval")}}
            <BsButton
              @type="primary"
              @onClick={{this.approveSelected.perform}}
              class="apply-approval fs-3"
            >
              {{t "approve"}}
            </BsButton>
          {{/if}}
          {{#if (eq this.approveRejectMode "disapproval")}}
            <BsButton
              @type="warning"
              @onClick={{this.disapproveSelected.perform}}
              class="apply-disapproval fs-3"
            >
              {{t "disapprove"}}
            </BsButton>
          {{/if}}
          <footer.close>
            {{t "cancel"}}
          </footer.close>
        </modal.footer>
      </StandardModal>
    {{/if}}
    {{#if this.enlargedImageSrc}}
      <Portal @target="modal">
        <div class="wie-enlarged-image position-absolute d-flex justify-content-center align-items-center w-100 h-100">
          <img class="h-100" src={{this.enlargedImageSrc}} />
          <div
            class="icon icon-fullscreen-exit close-image align-self-start pointer rounded text-body bg-white p-3 m-2"
            {{on "click" (fn (mut this.enlargedImageSrc) null)}}
          />
        </div>
      </Portal>
    {{/if}}
    {{#if this.showDeploymentModal}}
      <StandardModal
        class="jem wie-deployment-modal"
        @onConfirm={{this.deploy.perform}}
        @onCancel={{fn (mut this.showDeploymentModal) false}}
      as |modal|>
        <modal.header @text="{{t "workInstruction"}} {{t "deployment"}}" />
        <modal.body>
          {{#if (eq @selectedCards.length 1)}}
            {{t "wie.deployment.single"}}
          {{else}}
            {{t "wie.deployment.multiple"}}
          {{/if}}
          <ul class="instruction-list">
            {{#each @selectedCards as |libraryItem|}}
              <li>
                {{libraryItem.name}}
              </li>
            {{/each}}
          </ul>
        </modal.body>
        <modal.footer/>
      </StandardModal>
    {{/if}}

    {{#if this.showConfigurationHistoryNotesModal}}
      <ConfigurationHistoryNotes
        @locations={{this.uniqueAssignedLocations}}
        @type={{this.wieConfigurationSettingsType}}
        @onConfirm={{fn (mut this.showConfigurationHistoryNotesModal) false}}
      />
    {{/if}}

    {{#if (or (not @isReadOnly) this.currentUser.isAdmin this.userIsApprover this.userIsDeployer)}}
      <Portal @target="multiselect-change-review">
        <div class="col-auto">
          <BsButton
            class="btn-review-all border-0 ms-1"
            @size="sm"
            @disabled={{not (this.canApprove @selectedCards)}}
            @onClick={{fn this.openReviewModal.perform "review"}}
          >
            {{t "reviewAll"}}
          </BsButton>
        </div>
        <div class="col-auto">
          <BsButton
            class="btn-approve-all border-0 ms-1"
            @size="sm"
            @disabled={{not (this.canApprove @selectedCards)}}
            @onClick={{fn this.openReviewModal.perform "approval"}}
          >
            {{t "approveAll"}}
          </BsButton>
        </div>
        <div class="col-auto">
          <BsButton
            class="btn-reject-all border-0 ms-1"
            @size="sm"
            @disabled={{not (this.canApprove @selectedCards)}}
            @onClick={{fn this.openReviewModal.perform "disapproval"}}
          >
            {{t "rejectAll"}}
          </BsButton>
        </div>
        <div class="col-auto">
          <BsButton
            class="btn-deploy-all border-0 ms-1"
            @size="sm"
            @disabled={{not (this.canDeploy @selectedCards)}}
            @onClick={{fn (mut this.showDeploymentModal) true}}
          >
            {{t "deployAll"}}
          </BsButton>
        </div>
      </Portal>
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
